// Redaction for things that leave the process: request logs and Sentry
// events. Pure and dependency-free so the API, the web app and the lambdas
// share one list.
//
// Why: @sentry/core's requestDataIntegration sends request headers, cookies
// and bodies by default (include.headers/cookies/data all default to true in
// 10.x). That puts X-API-Key, Authorization, the sessionId / rememberMe
// cookies and login passwords into every error and transaction event.
// One-time tokens also travel in URL paths (GET /api/v1/user/once/<token>,
// the web /reset/<token> and /magic/<token> pages), so URLs are redacted too.

export const FILTERED = '[Filtered]'

const SECRET_HEADERS = new Set([
  'x-api-key',
  'authorization',
  'proxy-authorization',
  'cookie',
  'set-cookie',
])

// Body fields (any depth) whose values are credentials.
const SECRET_FIELD =
  /^(password\w*|new_?password|token|otp|csrf|api[-_]?key|x-api-key|secret\w*|authorization|credentials)$/i

// A path segment that follows one of these is a live one-time token.
const TOKEN_PATH = /(\/(?:user\/once|reset|magic)\/)[^/?#\s"'<>]+/gi

export const redactTokenPaths = (s: string): string =>
  s.replace(TOKEN_PATH, `$1${FILTERED}`)

type Obj = Record<string, unknown>

const isObj = (v: unknown): v is Obj =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

// Only JSON-shaped data is walked: never class instances (the SDK keeps live
// Scope / client objects in sdkProcessingMetadata, which is not sent) and
// never a frozen object.
const isPlain = (v: unknown): v is Obj | unknown[] => {
  if (typeof v !== 'object' || v === null || Object.isFrozen(v)) return false
  if (Array.isArray(v)) return true
  const proto: unknown = Object.getPrototypeOf(v)
  return proto === Object.prototype || proto === null
}

const SKIP_KEYS = new Set(['sdkProcessingMetadata'])

// Replace one-time tokens in every string reachable from `value`.
const redactStringsDeep = (value: unknown, seen: WeakSet<object>): unknown => {
  if (typeof value === 'string') return redactTokenPaths(value)
  if (!isPlain(value) || seen.has(value)) return value
  seen.add(value)
  const o = value as Obj
  for (const k of Object.keys(o)) {
    if (SKIP_KEYS.has(k)) continue
    const next = redactStringsDeep(o[k], seen)
    if (next !== o[k]) o[k] = next
  }
  return value
}

/**
 * Replace one-time tokens in every string of a JSON-shaped value, in place
 * (analytics payloads, Sentry replay events). Returns the value.
 */
export const redactTokenPathsDeep = <T>(value: T): T =>
  redactStringsDeep(value, new WeakSet()) as T

/** True when a URL or path carries a one-time token segment. */
export const hasTokenPath = (s: string): boolean => redactTokenPaths(s) !== s

const redactFieldsDeep = (value: unknown, seen: WeakSet<object>): unknown => {
  if (!isPlain(value) || seen.has(value)) return value
  seen.add(value)
  const o = value as Obj
  const isArray = Array.isArray(value)
  for (const k of Object.keys(o)) {
    const next =
      !isArray && SECRET_FIELD.test(k) ? FILTERED : redactFieldsDeep(o[k], seen)
    if (next !== o[k]) o[k] = next
  }
  return value
}

const redactFormBody = (s: string): string =>
  s.replace(/(^|&)([^=&]+)=([^&]*)/g, (m, sep: string, k: string) => {
    let name = k
    try {
      name = decodeURIComponent(k)
    } catch {
      // keep the raw name
    }
    return SECRET_FIELD.test(name) ? `${sep}${k}=${FILTERED}` : m
  })

const redactBody = (data: unknown): unknown => {
  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data)
      if (typeof parsed === 'object' && parsed !== null)
        return JSON.stringify(redactFieldsDeep(parsed, new WeakSet()))
    } catch {
      // not JSON
    }
    return redactFormBody(data)
  }
  return redactFieldsDeep(data, new WeakSet())
}

/**
 * Scrub a Sentry event (error or transaction) in place and return it. Use as
 * both `beforeSend` and `beforeSendTransaction`.
 *
 * - drops credential headers (X-API-Key, Authorization, Cookie, ...)
 * - replaces every cookie value (sessionId, rememberMe, the OAuth handshake
 *   cookies) with [Filtered]
 * - redacts credential fields in the request body (password, token, csrf, ...)
 * - redacts one-time tokens in URL paths anywhere in the event (request URL,
 *   transaction name, breadcrumbs, spans, messages)
 */
export const scrubSentryEvent = <T>(event: T): T => {
  if (!isObj(event)) return event
  const request = event.request
  if (isObj(request)) {
    if (isObj(request.headers)) {
      for (const h of Object.keys(request.headers))
        if (SECRET_HEADERS.has(h.toLowerCase())) delete request.headers[h]
    }
    if (isObj(request.cookies)) {
      for (const c of Object.keys(request.cookies))
        request.cookies[c] = FILTERED
    } else if (typeof request.cookies === 'string') {
      request.cookies = FILTERED
    }
    if ('data' in request && request.data != null)
      request.data = redactBody(request.data)
  }
  redactStringsDeep(event, new WeakSet())
  return event
}
